import { jsx as _jsx, jsxs as _jsxs } from "theme-ui/jsx-runtime";
import { Box, Flex } from "@theme-ui/components";
import React, { useEffect, useState } from "react";
import useMeasure from "react-use-measure";
import { ResizeObserver } from "@juggle/resize-observer";
import { AnimatePresence, motion } from "framer-motion";
import { StandardXAxisPadding, Label3, enterObjectTransitionProperties, EditorialXAxisPadding, SmallArrowUp, SmallArrowDown, HeadingRegularVariants } from "../../index";
//In page navigation seen in filters and content
export const InPageBar = ({ 
/**
 * Label of the in page navigation, to give context to the user on what it controls
 */
title, 
/**
 * Additional space for further context, like number of sections
 */
subtitle, 
/**
 * Whether to align the links to the right of the bar
 */
alignLinksRight = false, 
/**
 * Pass the character count of each child item to the component so it can manage when to show the view more links.
 */
itemLengths, 
/**
 * Translation for the view more button
 */
translations, children }) => {
    const [menuOpen, toggleMenu] = useState(false);
    const [ref, bounds] = useMeasure({ polyfill: ResizeObserver });
    const [containerRef, containerBounds] = useMeasure({
        polyfill: ResizeObserver
    });
    const [{ menuItems, overflowItems }, menuSize] = useState({
        menuItems: children,
        overflowItems: []
    });
    useEffect(() => {
        if (itemLengths) {
            if (containerBounds.width > 500 && children) {
                const newMaxWidth = containerBounds.width / 13;
                // loop over itemlengths and get break point
                let itemsCharacterCountToIndex = 0;
                const visibleArray = [];
                const overflowArray = [];
                React.Children.forEach(children, (item, idx) => {
                    // what count are we up to
                    itemsCharacterCountToIndex += Number(itemLengths[idx]);
                    // its too big so split the items into to array
                    if (itemsCharacterCountToIndex >= newMaxWidth) {
                        overflowArray.push(item);
                    }
                    else {
                        visibleArray.push(item);
                    }
                });
                menuSize({
                    menuItems: visibleArray,
                    overflowItems: overflowArray
                });
                // check that there is something in the overflow, if not shut the menu if its open
                if (Object.keys(overflowItems).length <= 0)
                    toggleMenu(false);
                //toggleMenu( false )
            }
            else {
                // reset for mobile
                menuSize({
                    menuItems: children,
                    overflowItems: []
                });
            }
        }
    }, [containerBounds.width]);
    return (_jsxs(Box, { sx: { width: "100%" }, children: [_jsxs(Flex, { sx: {
                    flexDirection: "row",
                    bg: "blue.2",
                    flex: 1,
                    px: StandardXAxisPadding
                }, children: [_jsxs(Flex, { sx: { alignItems: "center", pr: 32, py: 24 }, children: [_jsxs(Flex, { sx: {
                                    flexDirection: "column",
                                    display: ["none", "none", "none", "block"]
                                }, children: [_jsx("h6", { sx: {
                                            variant: `text.${HeadingRegularVariants.sixth}`,
                                            mr: 24,
                                            color: "white.0"
                                        }, children: title }), subtitle && (_jsx(Label3, { sx: { mt: 4, color: "grey.3" }, children: subtitle }))] }), _jsxs(Box, { as: "button", "aria-expanded": menuOpen, sx: {
                                    display: ["flex", "flex", "flex", "none"],
                                    alignItems: "center",
                                    color: "white.0",
                                    p: 24,
                                    variant: "buttons.accessible"
                                }, onClick: () => toggleMenu(!menuOpen), children: [_jsxs(Flex, { sx: {
                                            flexDirection: "column",
                                            alignItems: "flex-start"
                                        }, children: [_jsx("h6", { sx: {
                                                    variant: `text.${HeadingRegularVariants.sixth}`,
                                                    mr: 24,
                                                    color: "white.0"
                                                }, children: title }), subtitle && (_jsx(Label3, { sx: { mt: 4, color: "grey.3" }, children: subtitle }))] }), menuOpen ? _jsx(SmallArrowUp, {}) : _jsx(SmallArrowDown, {})] })] }), _jsxs(Flex, { ref: containerRef, sx: {
                            display: ["none", "none", "none", "flex"],
                            width: "100%",
                            flex: 1,
                            justifyContent: [
                                "flex-start",
                                null,
                                null,
                                alignLinksRight ? "flex-end" : "flex-start"
                            ],
                            "& > div": {
                                display: "flex"
                            },
                            "& a": {
                                variant: "text.label2",
                                textDecoration: "none",
                                color: "white.0",
                                px: 24,
                                flex: 1,
                                display: "flex",
                                alignItems: "center"
                            },
                            "& > div:hover a": {
                                color: "white.0",
                                bg: "blue.1"
                            }
                        }, children: [menuItems, _jsx(Box, { as: "button", className: "in-page-menu-items", "aria-expanded": menuOpen, sx: {
                                    variant: "buttons.accessible",
                                    color: "white.0",
                                    textDecoration: "none",
                                    px: 24,
                                    alignItems: "center",
                                    cursor: "pointer",
                                    display: Object.keys(overflowItems).length > 0 ? "flex" : "none",
                                    bg: "blue.1",
                                    ml: "auto",
                                    ":hover": {
                                        color: "white.0",
                                        bg: "black.1"
                                    }
                                }, onClick: () => toggleMenu(!menuOpen), children: _jsxs(Box, { sx: { variant: "text.label2", "& > svg": { ml: 8 } }, children: [translations.viewMore, menuOpen ? _jsx(SmallArrowUp, {}) : _jsx(SmallArrowDown, {})] }) })] })] }), _jsx(AnimatePresence, { children: menuOpen && (_jsx(motion.div, { hidden: !menuOpen, style: {
                        position: "absolute",
                        width: Object.keys(overflowItems).length > 0 ? "auto" : "100%",
                        zIndex: 11
                    }, initial: { opacity: 0, height: 0 }, animate: { opacity: 1, height: bounds.height + 24 }, exit: { opacity: 0, height: 0 }, custom: enterObjectTransitionProperties, sx: {
                        right: Object.keys(overflowItems).length > 0 ? StandardXAxisPadding : 0
                    }, children: _jsx(Flex, { ref: ref, sx: {
                            py: 24,
                            px: Object.keys(overflowItems).length > 0
                                ? 32
                                : EditorialXAxisPadding,
                            bg: "white.0",
                            boxShadow: "small",
                            width: "100%",
                            flexDirection: "column",
                            "& > * + *": {
                                mt: 16
                            },
                            "& a": {
                                color: "blue.0",
                                variant: "text.label2",
                                textDecoration: "none"
                            }
                        }, children: Object.keys(overflowItems).length > 0 ? overflowItems : children }) }, "inpage-menu")) })] }));
};
//# sourceMappingURL=InPageBar.js.map